/**
 * Email Verification Page
 * Public route (no authentication required)
 * User clicks activation link from email: /auth/verify-email?userId=X&token=Y
 *
 * @module Features/Auth/Pages
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Alert,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useVerifyEmailMutation } from '../../UserManagement/Service/usersApi';
import { useSnackbar } from 'notistack';

/**
 * Email Verification Page Component
 * Public route accessible without authentication
 */
const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [verifyEmail] = useVerifyEmailMutation();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyUserEmail = async () => {
      try {
        const userIdParam = searchParams.get('userId');
        const tokenParam = searchParams.get('token');

        // Validate params
        if (!userIdParam || !tokenParam) {
          setStatus('error');
          setMessage('Missing verification parameters. Invalid link.');
          console.error('❌ Invalid verification link');
          return;
        }

        const userId = parseInt(userIdParam, 10);
        if (isNaN(userId)) {
          setStatus('error');
          setMessage('Invalid user ID in verification link.');
          console.error('❌ Invalid verification link');
          return;
        }

        // Call verification API
        const response = await verifyEmail({
          userId,
          token: tokenParam,
        }).unwrap();

        if (response.success) {
          setStatus('success');
          setMessage(response.message || 'Email zweryfikowany pomyślnie!');
          enqueueSnackbar('Email zweryfikowany pomyślnie!', { variant: 'success' });
          console.log('✅ Email verified successfully!');
        } else {
          setStatus('error');
          setMessage(response.message || 'Weryfikacja emaila nie powiodła się. Spróbuj ponownie.');
          enqueueSnackbar(response.message || 'Weryfikacja emaila nie powiodła się', { variant: 'error' });
          console.error('❌ Error:', response.message || 'Verification failed');
        }
      } catch (error: any) {
        setStatus('error');
        const errorMessage =
          error?.data?.message ||
          error?.message ||
          'Weryfikacja emaila nie powiodła się. Token może być wygasły lub niepoprawny.';
        setMessage(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
        console.error('❌ Error:', errorMessage);
      }
    };

    // Only verify once on mount
    verifyUserEmail();
  }, [searchParams, verifyEmail, enqueueSnackbar]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        py: 3,
      }}
    >
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          {status === 'loading' ? (
            <Stack spacing={2} sx={{ alignItems: 'center' }}>
              <CircularProgress size={60} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Verifying Email...
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Please wait while we verify your email address.
              </Typography>
            </Stack>
          ) : status === 'success' ? (
            <Stack spacing={2} sx={{ alignItems: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                ✓ Email Verified!
              </Typography>
              <Alert severity="success">
                <Typography variant="body2">
                  Your account is now active and ready to use.
                </Typography>
              </Alert>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                {message}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/auth/login')}
                sx={{ mt: 2 }}
              >
                Go to Login
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ alignItems: 'center' }}>
              <ErrorIcon sx={{ fontSize: 60, color: 'error.main' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.main' }}>
                ✗ Verification Failed
              </Typography>
              <Alert severity="error">
                <Typography variant="body2">{message}</Typography>
              </Alert>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Common reasons:
              </Typography>
              <Box sx={{ textAlign: 'left', bgcolor: '#f9f9f9', p: 2, borderRadius: 1 }}>
                <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                  • Verification link has expired (valid for 24 hours)
                </Typography>
                <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                  • Invalid or tampered verification token
                </Typography>
                <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                  • Account already verified
                </Typography>
                <Typography variant="caption" component="div">
                  • Check your email again or contact administrator
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/auth/login')}>
                  Go to Login
                </Button>
                <Button variant="outlined" color="error" onClick={() => navigate('/')}>
                  Go Home
                </Button>
              </Stack>
            </Stack>
          )}
        </Paper>

        {/* Debug info (remove in production) */}
        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f0f0f0', borderRadius: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Debug: Status = {status}, Parameters present =
              {searchParams.get('userId') ? 'userId' : ''}{' '}
              {searchParams.get('token') ? '+ token' : ''}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default VerifyEmailPage;
